import { HttpEventType, HttpResponse } from '@angular/common/http';
import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, forkJoin, last, map, Observable, of, switchMap } from 'rxjs';
import {
  CollaborationV2,
  CollaborationsGuardedDataService,
  CreateMessageAttachment,
  Message,
  MessageContentAttachmentMediaTypeEnum,
  ScopedCollabQuickAction,
} from '@ffd/api-client';
import {
  Attachment,
  CollabMessageAttachment,
  CollaborationMessage,
  collaborationsActions,
  CollaborationScreenRecordingService,
  getSortedMessagesAscending,
  mapApiMessageToCollaborationMessage,
  mapMessages,
  mapMessagesWithThreadId,
  MessageVM,
  ScreenRecordingOrigin,
} from '@libs/collaboration';
import { FileUploadService } from '@libs/shared-services';
import { selectCurrentUserProfile } from '@libs/store';
import { AssistantChatWindowService } from './assistant-chat-window.service';
import { AssistantLauncherService } from './assistant-launcher.service';

const MESSAGE_LIST_LIMIT = 20;
const THREAD_MESSAGE_LIST_LIMIT = 20;

export interface AssistantSendMessageRequest {
  text: string;
  threadMessageId?: string;
  /**
   * Attachments added through the composer's photo/video picker or a named quick action
   * (`start-screen-recording`, `attach-image`), although the assistant composer hides its regular attachment buttons.
   */
  attachments?: CollabMessageAttachment[];
}

function mapToCreateMessageAttachment(attachment: CollabMessageAttachment): CreateMessageAttachment {
  return {
    fileId: attachment.id,
    mediaType: attachment.mediaType,
    category: attachment.category,
    date: attachment.date,
    toothPositions: attachment.toothPositions,
    toothPositionString: attachment.toothPositionString,
  };
}

@Injectable({ providedIn: 'root' })
export class AssistantMessagesService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly collaborationsGuardedDataService = inject(CollaborationsGuardedDataService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly screenRecordingService = inject(CollaborationScreenRecordingService);
  private readonly chatWindowService = inject(AssistantChatWindowService);
  private readonly assistantLauncherService = inject(AssistantLauncherService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly profile = this.store.selectSignal(selectCurrentUserProfile);
  public readonly collabId = this.assistantLauncherService.collabId;
  private readonly userId = computed(() => this.profile()?.id);

  // eslint-disable-next-line unicorn/no-useless-undefined
  private readonly collaboration = signal<CollaborationV2 | undefined>(undefined);
  private readonly messagesAscending = signal<CollaborationMessage[]>([]);
  private readonly noMoreMessagesSignal = signal(false);
  private readonly isLoadingSignal = signal(false);
  // eslint-disable-next-line unicorn/no-useless-undefined
  private readonly newMessageMarkerBeforeMessageIdSignal = signal<string | undefined>(undefined);

  // eslint-disable-next-line unicorn/no-useless-undefined
  private readonly threadParentMessageId = signal<string | undefined>(undefined);
  private readonly threadMessagesAscending = signal<CollaborationMessage[]>([]);
  private readonly noMoreThreadMessagesSignal = signal(false);

  // eslint-disable-next-line unicorn/no-useless-undefined
  private loadedCollabId: string | undefined = undefined;
  private initialized = false;

  private readonly reloadOnCollabChangeEffect = effect(() => {
    this.collabId();
    this.loadIfNeeded();
  });

  public readonly participantCount = computed(() => {
    const participants = this.collaboration()?.participants;
    if (!participants) {
      return 0;
    }
    return participants.employees.length + (participants.patients?.length ?? 0) + (participants.emails?.length ?? 0);
  });

  public readonly messages = computed<MessageVM[]>(() => mapMessages(this.messagesAscending(), this.collaboration(), this.userId()));

  /** Quick actions of the personal assistant collab, merged and ordered by the API (AC-11). */
  public readonly collabQuickActions = computed<ScopedCollabQuickAction[]>(() => this.collaboration()?.quickActions ?? []);
  public readonly noMoreMessages = this.noMoreMessagesSignal.asReadonly();
  public readonly isLoading = this.isLoadingSignal.asReadonly();
  public readonly newMessageMarkerBeforeMessageId = this.newMessageMarkerBeforeMessageIdSignal.asReadonly();

  // The launcher badge counts the messages from others since the user last had the chat open.
  public readonly unreadCount = computed(() => {
    if (this.chatWindowService.isOpen() || !this.assistantLauncherService.hasUnreadMessage()) {
      return 0;
    }
    const messages = this.messagesAscending();
    const markerId = this.newMessageMarkerBeforeMessageIdSignal();
    const fromIndex = markerId ? messages.findIndex((message) => message.id === markerId) : -1;
    const count = fromIndex === -1 ? 0 : messages.slice(fromIndex).filter((message) => message.senderId !== this.userId()).length;
    // The global store can know about an unread message before the messages are loaded - never show less than one.
    return Math.max(count, 1);
  });

  public readonly threadMessages = computed<MessageVM[]>(() =>
    mapMessages(this.threadMessagesAscending(), this.collaboration(), this.userId(), true),
  );
  public readonly noMoreThreadMessages = this.noMoreThreadMessagesSignal.asReadonly();

  // Both the main and the FFD Notes layout call this, and root subscriptions must not stack up.
  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.listenToReceiveMessage();
    this.listenToUpdateMessage();
    this.listenToDeleteMessage();
    this.listenToFinishedScreenRecording();
  }

  public loadIfNeeded(): void {
    const collabId = this.collabId();
    if (this.loadedCollabId === collabId) {
      return;
    }
    this.resetState();
    // resetState() wiped the messages, so the collaboration they belonged to is no longer loaded.
    // Switching to a site without an assistant collab (e.g. showcase) lands here, and leaving this
    // set to the previous collab id would make the guard above skip the reload when the user comes
    // back to that same site, leaving the chat window empty.
    // eslint-disable-next-line unicorn/no-useless-undefined
    this.loadedCollabId = undefined;
    if (!collabId) {
      return;
    }
    this.loadedCollabId = collabId;
    this.isLoadingSignal.set(true);

    this.collaborationsGuardedDataService
      .getCollaborationV2(collabId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collaboration) => {
          this.collaboration.set(collaboration);
          this.fetchMessages(collaboration);
        },
        error: (error) => {
          console.error(error);
          this.isLoadingSignal.set(false);
        },
      });
  }

  private resetState(): void {
    // eslint-disable-next-line unicorn/no-useless-undefined
    this.collaboration.set(undefined);
    this.messagesAscending.set([]);
    this.noMoreMessagesSignal.set(false);
    this.isLoadingSignal.set(false);
    // eslint-disable-next-line unicorn/no-useless-undefined
    this.newMessageMarkerBeforeMessageIdSignal.set(undefined);
    // eslint-disable-next-line unicorn/no-useless-undefined
    this.threadParentMessageId.set(undefined);
    this.threadMessagesAscending.set([]);
    this.noMoreThreadMessagesSignal.set(false);
  }

  public loadOlderMessages(): void {
    const collab = this.collaboration();
    if (!collab) {
      return;
    }
    this.fetchMessages(collab);
  }

  // Messages can be preloaded before the window opens, so loading must not imply reading.
  // Call this when the window opens to mark already-loaded messages as read.
  public markAsReadIfWindowOpen(): void {
    if (!this.chatWindowService.isOpen()) {
      return;
    }

    const collab = this.collaboration();
    const lastMessage = this.messagesAscending().at(-1);
    // The launcher's unread dot lives in the global store, which can know about an unread message the
    // locally cached collaboration doesn't (e.g. it arrived over the websocket after the collab was loaded).
    const isUnread = (collab?.hasUnreadMessage ?? false) || this.assistantLauncherService.hasUnreadMessage();
    if (isUnread && lastMessage) {
      this.markMessageAsRead(lastMessage.id);
      this.collaboration.update((c) => (c ? { ...c, hasUnreadMessage: false } : c));
      this.assistantLauncherService.hidePreview();
    }
  }

  // The "New" divider stays visible for the whole session the window is open, so it is cleared when
  // the window closes rather than on open. This service is root-provided and its loaded messages
  // outlive the window, so without this the divider would still be there on the next open, pointing
  // at messages that were already marked as read - the launcher's unread dot avoids this because the
  // store clears it via personalAssistantCollaborationBecameRead.
  // A message marked as unread during the session is still unread after the window closes, so its
  // marker stays - the launcher's unread counter counts from it.
  public clearNewMessageMarker(): void {
    if (this.collaboration()?.hasUnreadMessage) {
      return;
    }
    // eslint-disable-next-line unicorn/no-useless-undefined
    this.newMessageMarkerBeforeMessageIdSignal.set(undefined);
  }

  // The same button starts and stops the recording. The finished recording reaches the assistant's composer through
  // screenRecordingSucceeded, which only the composer with the Assistant origin picks up.
  public toggleScreenRecording(siteId: string | undefined): void {
    if (this.screenRecordingService.recordingStatus() === 'recording') {
      this.store.dispatch(collaborationsActions.screenRecordingStopRequested());
      return;
    }

    const collabId = this.collabId();
    if (!collabId) {
      return;
    }

    this.store.dispatch(
      collaborationsActions.screenRecordingRequested({ collaborationId: collabId, siteId, origin: ScreenRecordingOrigin.Assistant }),
    );
  }

  public sendMessage(request: AssistantSendMessageRequest): void {
    const collabId = this.collabId();
    const userId = this.userId();
    const attachments = (request.attachments ?? []).filter((attachment) => !!attachment.file);
    if (!collabId || !userId || (!request.text.trim() && attachments.length === 0)) {
      return;
    }

    // Uploading the attachments may take a while, so the message is shown as sending right away.
    const sendingInProgressMessage = this.createSendingInProgressMessage(request.text, userId, attachments);
    this.addNewMessage(sendingInProgressMessage);
    const isThreadReply = !!request.threadMessageId && request.threadMessageId === this.threadParentMessageId();
    if (isThreadReply) {
      this.addNewThreadMessage(sendingInProgressMessage);
    }

    this.uploadAttachments(sendingInProgressMessage.attachments ?? [])
      .pipe(
        switchMap((uploadedAttachments) =>
          this.collaborationsGuardedDataService
            .sendMessage(collabId, {
              text: request.text,
              parentId: request.threadMessageId,
              attachments:
                uploadedAttachments.length > 0
                  ? uploadedAttachments.map((attachment) => mapToCreateMessageAttachment(attachment))
                  : undefined,
            })
            .pipe(map((sentMessage) => ({ sentMessage, uploadedAttachments }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ sentMessage, uploadedAttachments }) => {
          this.removeSendingInProgressMessage(sendingInProgressMessage.id);
          const message = mapApiMessageToCollaborationMessage(sentMessage);
          // Keep the local copies (same ids, local file urls) so the images show without waiting for the server thumbnails.
          const messageWithAttachments = uploadedAttachments.length > 0 ? { ...message, attachments: uploadedAttachments } : message;
          this.upsertMessage(messageWithAttachments);

          if (isThreadReply) {
            this.removeSendingInProgressThreadMessage(sendingInProgressMessage.id);
            this.upsertThreadMessage(messageWithAttachments);
          }
        },
        error: (error) => {
          console.error(error);
          this.errorTempMessage(sendingInProgressMessage.id);
        },
      });
  }

  public retrySendMessage(messageId: string): void {
    const retryMessage = this.messagesAscending().find((message) => message.id === messageId);
    if (!retryMessage) {
      return;
    }
    this.removeSendingInProgressMessage(messageId);
    this.sendMessage({ text: retryMessage.text ?? '', attachments: retryMessage.attachments });
  }

  public unsendMessage(messageId: string): void {
    const collabId = this.collabId();
    if (!collabId) {
      return;
    }

    this.collaborationsGuardedDataService
      .deleteMessage(collabId, messageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.applyDeletedMessageToState(messageId),
        error: (error) => console.error(error),
      });
  }

  public editMessage(messageId: string, text: string): void {
    const collabId = this.collabId();
    if (!collabId) {
      return;
    }
    const originalText = this.messagesAscending().find((message) => message.id === messageId)?.text;
    this.applyOptimisticMessageEdit(messageId, text);

    this.collaborationsGuardedDataService
      .editMessage(collabId, messageId, { text })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedMessage) => this.clearMessageEditSaving(messageId, updatedMessage),
        error: (error) => {
          console.error(error);
          this.revertMessageEdit(messageId, originalText);
        },
      });
  }

  public markMessageAsUnread(messageId: string): void {
    const collabId = this.collabId();
    if (!collabId) {
      return;
    }

    this.collaborationsGuardedDataService
      .markMessageAsUnread(collabId, messageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const currentMessages = this.messagesAscending();
          const messageIndex = currentMessages.findIndex((message) => message.id === messageId);
          const lastReadMessageId = messageIndex > 0 ? currentMessages[messageIndex - 1].id : undefined;

          this.newMessageMarkerBeforeMessageIdSignal.set(messageId);
          this.collaboration.update((collab) => (collab ? { ...collab, hasUnreadMessage: true, lastReadMessageId } : collab));
          // The launcher button's unread dot reads from the global collaborations store, not from
          // this service's local state - keep it in sync.
          this.store.dispatch(collaborationsActions.personalAssistantCollaborationBecameUnread({ collaborationId: collabId }));

          this.showPreviewFromUnreadMessageId(messageId);
        },
        error: (error) => console.error(error),
      });
  }

  public openThread(parentMessageId: string): void {
    this.threadParentMessageId.set(parentMessageId);
    this.threadMessagesAscending.set([]);
    this.noMoreThreadMessagesSignal.set(false);
    this.loadOlderThreadMessages();
  }

  public clearThreadMessages(): void {
    this.threadParentMessageId.set(undefined);
    this.threadMessagesAscending.set([]);
    this.noMoreThreadMessagesSignal.set(false);
  }

  public loadOlderThreadMessages(): void {
    const collabId = this.collabId();
    const parentMessageId = this.threadParentMessageId();
    if (!collabId || !parentMessageId || this.noMoreThreadMessagesSignal()) {
      return;
    }

    const before = this.threadMessagesAscending()[0]?.id;

    this.collaborationsGuardedDataService
      .getMessageReplies(collabId, parentMessageId, THREAD_MESSAGE_LIST_LIMIT, before)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const mergedSortedMessages = mapMessagesWithThreadId(
            getSortedMessagesAscending([
              ...response.messages.map((message) => mapApiMessageToCollaborationMessage(message)),
              ...this.threadMessagesAscending(),
            ]),
          );

          this.threadMessagesAscending.set(mergedSortedMessages);
          this.noMoreThreadMessagesSignal.set(response.messages.length < THREAD_MESSAGE_LIST_LIMIT);
        },
        error: (error) => console.error(error),
      });
  }

  private fetchMessages(collab: CollaborationV2): void {
    if (this.noMoreMessagesSignal()) {
      return;
    }
    const before = this.messagesAscending()[0]?.id;

    this.collaborationsGuardedDataService
      .getMessagesInCollaboration(collab.id, MESSAGE_LIST_LIMIT, before)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (newMessages) => {
          const mergedSortedMessages = mapMessagesWithThreadId(
            getSortedMessagesAscending([
              ...newMessages.list.map((message) => mapApiMessageToCollaborationMessage(message)),
              ...this.messagesAscending(),
            ]),
          );

          this.messagesAscending.set(mergedSortedMessages);
          this.noMoreMessagesSignal.set(newMessages.list.length < MESSAGE_LIST_LIMIT);

          // lastReadMessageId can sit further back than what's loaded (marked unread after the
          // fact) - keep paging back until it's found, or the unread range below starts too late.
          const isStillUnread = this.collaboration()?.hasUnreadMessage ?? false;
          const lastReadMessageIsUnresolved =
            !!collab.lastReadMessageId && !mergedSortedMessages.some((message) => message.id === collab.lastReadMessageId);
          if (isStillUnread && lastReadMessageIsUnresolved && !this.noMoreMessagesSignal()) {
            this.fetchMessages(collab);
            return;
          }

          const lastReadMessageIndex = mergedSortedMessages.findIndex((message) => message.id === collab.lastReadMessageId);
          const firstUnreadMessageId =
            lastReadMessageIndex > -1 ? mergedSortedMessages[lastReadMessageIndex + 1]?.id : mergedSortedMessages[0]?.id;

          this.newMessageMarkerBeforeMessageIdSignal.set(isStillUnread ? firstUnreadMessageId : undefined);

          if (isStillUnread) {
            this.showPreviewFromUnreadMessageId(firstUnreadMessageId);
          }
          this.markAsReadIfWindowOpen();

          this.isLoadingSignal.set(false);
        },
        error: (error) => {
          console.error(error);
          this.isLoadingSignal.set(false);
        },
      });
  }

  private markMessageAsRead(messageId: string): void {
    const collabId = this.collabId();
    if (!collabId) {
      return;
    }

    this.collaborationsGuardedDataService
      .markMessageAsRead(collabId, messageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Mirrors markMessageAsUnread: the launcher's unread dot reads from the global store, keep it in sync.
        next: () => this.store.dispatch(collaborationsActions.personalAssistantCollaborationBecameRead({ collaborationId: collabId })),
        error: (error) => console.error(error),
      });
  }

  private showPreviewFromUnreadMessageId(unreadFromMessageId: string | undefined): void {
    if (!unreadFromMessageId) {
      return;
    }

    const messages = this.messages();
    const fromIndex = messages.findIndex((message) => message.id === unreadFromMessageId);
    if (fromIndex === -1) {
      return;
    }

    const lastUnreadFromOtherUser = messages
      .slice(fromIndex)
      .reverse()
      .find((message) => !message.sender.isSelf && !message.isSystemMessage && (!!message.text || !!this.findVideo(message)));
    if (lastUnreadFromOtherUser) {
      const video = this.findVideo(lastUnreadFromOtherUser);
      this.assistantLauncherService.showPreview({
        senderFirstName: lastUnreadFromOtherUser.sender.firstName,
        senderLastName: lastUnreadFromOtherUser.sender.lastName,
        text: lastUnreadFromOtherUser.text ?? '',
        video: video && {
          messageId: lastUnreadFromOtherUser.id,
          attachmentId: video.id,
          thumbnailUrl: video.thumbnailUrl ?? undefined,
        },
      });
    }
  }

  // The preview shows only the first video of a message - images are not shown in it.
  private findVideo(message: MessageVM): Attachment | undefined {
    return message.attachments?.allAttachments.find(({ mediaType }) => mediaType === MessageContentAttachmentMediaTypeEnum.RECORDING);
  }

  private addNewMessage(message: CollaborationMessage): void {
    const messages = this.messagesAscending();
    if (messages.some((existing) => existing.id === message.id)) {
      return;
    }

    const parentId = message.parentMessage?.id;
    const messagesWithNewMessage = [...messages, message];
    this.messagesAscending.set(
      parentId
        ? messagesWithNewMessage.map((existing) =>
            existing.id === parentId ? { ...existing, replyCount: (existing.replyCount ?? 0) + 1 } : existing,
          )
        : messagesWithNewMessage,
    );
  }

  private addNewThreadMessage(message: CollaborationMessage): void {
    const threadMessages = this.threadMessagesAscending();
    if (threadMessages.some((existing) => existing.id === message.id)) {
      return;
    }
    this.threadMessagesAscending.set(getSortedMessagesAscending([...threadMessages, message]));
  }

  // The websocket echo of an own message can arrive before the API response. The response's copy replaces it, because
  // only that one carries the local file urls of the attachments.
  private upsertMessage(message: CollaborationMessage): void {
    if (this.messagesAscending().some((existing) => existing.id === message.id)) {
      this.messagesAscending.update((messages) => messages.map((existing) => (existing.id === message.id ? message : existing)));
      return;
    }
    this.addNewMessage(message);
  }

  private upsertThreadMessage(message: CollaborationMessage): void {
    if (this.threadMessagesAscending().some((existing) => existing.id === message.id)) {
      this.threadMessagesAscending.update((messages) => messages.map((existing) => (existing.id === message.id ? message : existing)));
      return;
    }
    this.addNewThreadMessage(message);
  }

  private uploadAttachments(attachments: CollabMessageAttachment[]): Observable<CollabMessageAttachment[]> {
    if (attachments.length === 0) {
      return of([]);
    }

    return forkJoin(
      attachments.map((attachment) =>
        this.fileUploadService.uploadCollaborationAttachment(attachment.file!).pipe(
          filter((event): event is HttpResponse<{ fileId: string }> => event.type === HttpEventType.Response),
          last(),
          map((response) => {
            if (!response.body) {
              throw new Error('No body in the attachment upload response');
            }

            return { ...attachment, id: response.body.fileId };
          }),
        ),
      ),
    );
  }

  private createSendingInProgressMessage(text: string, userId: string, attachments: CollabMessageAttachment[]): CollaborationMessage {
    return {
      id: `temp-${crypto.randomUUID()}`,
      sentAt: new Date().toISOString(),
      senderId: userId,
      text,
      attachments:
        attachments.length > 0
          ? attachments.map((attachment) => ({
              id: `temp-${crypto.randomUUID()}`,
              mediaType: attachment.mediaType,
              category: attachment.category,
              url: attachment.url,
              thumbnailUrl: attachment.url,
              date: attachment.date,
              file: attachment.file,
            }))
          : undefined,
      isSending: true,
    };
  }

  private removeSendingInProgressMessage(sendingInProgressMessageId: string): void {
    this.messagesAscending.update((messages) => messages.filter((message) => message.id !== sendingInProgressMessageId));
  }

  private removeSendingInProgressThreadMessage(sendingInProgressMessageId: string): void {
    this.threadMessagesAscending.update((messages) => messages.filter((message) => message.id !== sendingInProgressMessageId));
  }

  private errorTempMessage(tempMessageId: string): void {
    const markFailed = (message: CollaborationMessage): CollaborationMessage =>
      message.id === tempMessageId ? { ...message, id: message.id.replace('temp', 'error'), isSending: false, isFailed: true } : message;

    if (this.messagesAscending().some((message) => message.id === tempMessageId)) {
      this.messagesAscending.update((messages) => messages.map((message) => markFailed(message)));
    }
    if (this.threadMessagesAscending().some((message) => message.id === tempMessageId)) {
      this.threadMessagesAscending.update((messages) => messages.map((message) => markFailed(message)));
    }
  }

  private isRelatedMessage(message: CollaborationMessage, messageId: string): boolean {
    return message.id === messageId || message.parentMessage?.id === messageId;
  }

  private applyDeletedMessageToState(messageId: string): void {
    const softDeletedAt = new Date().toISOString();
    const applyDelete = (message: CollaborationMessage): CollaborationMessage => {
      if (message.id === messageId) {
        return { ...message, text: undefined, attachments: undefined, softDeletedAt };
      }
      if (message.parentMessage?.id === messageId) {
        return { ...message, parentMessage: { ...message.parentMessage, text: undefined, attachments: undefined, softDeletedAt } };
      }
      return message;
    };

    if (this.messagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.messagesAscending.update((messages) => messages.map((message) => applyDelete(message)));
    }
    if (this.threadMessagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.threadMessagesAscending.update((messages) => messages.map((message) => applyDelete(message)));
    }
  }

  private applyMessageEditToMessages(
    messages: CollaborationMessage[],
    messageId: string,
    text: string | undefined,
    isEditSaving: boolean,
  ): CollaborationMessage[] {
    return messages.map((message) => {
      if (message.id === messageId) {
        return { ...message, text, isEditSaving };
      }
      if (message.parentMessage?.id === messageId) {
        return { ...message, parentMessage: { ...message.parentMessage, text } };
      }
      return message;
    });
  }

  private applyOptimisticMessageEdit(messageId: string, text: string): void {
    if (this.messagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.messagesAscending.update((messages) => this.applyMessageEditToMessages(messages, messageId, text, true));
    }
    if (this.threadMessagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.threadMessagesAscending.update((messages) => this.applyMessageEditToMessages(messages, messageId, text, true));
    }
  }

  private clearMessageEditSaving(messageId: string, updatedMessage: Message): void {
    const editHistory = updatedMessage.history?.map((entry) => entry.text);
    if (this.messagesAscending().some((message) => message.id === messageId)) {
      this.messagesAscending.update((messages) =>
        messages.map((message) => (message.id === messageId ? { ...message, isEditSaving: false, isEdited: true, editHistory } : message)),
      );
    }
    if (this.threadMessagesAscending().some((message) => message.id === messageId)) {
      this.threadMessagesAscending.update((messages) =>
        messages.map((message) => (message.id === messageId ? { ...message, isEditSaving: false, isEdited: true, editHistory } : message)),
      );
    }
  }

  private revertMessageEdit(messageId: string, originalText: string | undefined): void {
    if (this.messagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.messagesAscending.update((messages) => this.applyMessageEditToMessages(messages, messageId, originalText, false));
    }
    if (this.threadMessagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.threadMessagesAscending.update((messages) => this.applyMessageEditToMessages(messages, messageId, originalText, false));
    }
  }

  private applyMessageUpdate(message: CollaborationMessage, messageId: string, text: string | undefined): CollaborationMessage {
    if (message.id === messageId) {
      return { ...message, text, isEdited: true, isEditSaving: false };
    }
    if (message.parentMessage?.id === messageId) {
      return { ...message, parentMessage: { ...message.parentMessage, text } };
    }
    return message;
  }

  private applyUpdatedMessageToState(messageId: string, text: string | undefined): void {
    if (this.messagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.messagesAscending.update((messages) => messages.map((message) => this.applyMessageUpdate(message, messageId, text)));
    }
    if (this.threadMessagesAscending().some((message) => this.isRelatedMessage(message, messageId))) {
      this.threadMessagesAscending.update((messages) => messages.map((message) => this.applyMessageUpdate(message, messageId, text)));
    }
  }

  // The user can close the window while recording, or stop the recording from the navbar, so the window is opened
  // again to show the finished recording waiting in the composer.
  private listenToFinishedScreenRecording(): void {
    this.actions$
      .pipe(
        ofType(collaborationsActions.screenRecordingSucceeded),
        filter(({ origin }) => origin === ScreenRecordingOrigin.Assistant),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.chatWindowService.open());
  }

  private listenToReceiveMessage(): void {
    this.actions$
      .pipe(
        ofType(collaborationsActions.wsReceiveMessage),
        filter(({ data }) => !!this.collabId() && data.collaborationId === this.collabId()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ data }) => {
        const message = mapApiMessageToCollaborationMessage(data.message);
        this.addNewMessage(message);

        if (data.message.parentMessage?.id && data.message.parentMessage.id === this.threadParentMessageId()) {
          this.addNewThreadMessage(message);
        }

        const messageSentByCurrentUser = data.message.senderId === this.userId();
        if (messageSentByCurrentUser) {
          return;
        }

        if (this.chatWindowService.isOpen()) {
          this.markMessageAsRead(data.message.id);
          this.collaboration.update((collab) => (collab ? { ...collab, hasUnreadMessage: false } : collab));
        } else {
          if (!this.newMessageMarkerBeforeMessageIdSignal()) {
            this.newMessageMarkerBeforeMessageIdSignal.set(data.message.id);
          }
          // The launcher button's unread dot reads from the global collaborations store, not from
          // this service's local state - keep it in sync (mirrors markMessageAsUnread).
          this.store.dispatch(collaborationsActions.personalAssistantCollaborationBecameUnread({ collaborationId: this.collabId()! }));
        }
      });
  }

  private listenToUpdateMessage(): void {
    this.actions$
      .pipe(
        ofType(collaborationsActions.wsUpdateMessage),
        filter(({ data }) => !!this.collabId() && data.collaborationId === this.collabId()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ data }) => this.applyUpdatedMessageToState(data.message.id, data.message.text));
  }

  private listenToDeleteMessage(): void {
    this.actions$
      .pipe(
        ofType(collaborationsActions.wsDeleteMessage),
        filter(({ data }) => !!this.collabId() && data.collaborationId === this.collabId()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ data }) => this.applyDeletedMessageToState(data.messageId));
  }
}
